/**
 * Form (conteúdo de slide-over) para emitir NF-e (modelo 55, mercadoria).
 *
 * Novo em FIN-9: a UI não expunha a criação de NF-e (modelo 55) — só NFS-e
 * e NFC-e. Consome o endpoint já existente POST /api/notas-fiscais
 * (criarNotaFiscal): cria a nota tipo=NFE PENDENTE a partir de um pedido
 * entregue; CFOP/natureza são opcionais (default vem da ConfiguracaoFiscal).
 * Gestor clica "Emitir" no detalhe para enviar ao gateway.
 */
import React, { useEffect, useState } from "react";
import { FiFilePlus } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import { useAuth } from "../../../context/AuthContext";
import { criarNotaFiscal } from "../../../services/fiscal";
import { Input, Select, Textarea } from "../../Orcamentos/styles";

export default function CriarNfeForm({ onCreated, onCancel }) {
  const { request } = useAuth();
  const [pedidos, setPedidos] = useState([]);
  const [loadingPedidos, setLoadingPedidos] = useState(false);
  const [d, setD] = useState({ pedido_id: "", cfop: "", natureza_operacao: "", observacao_fiscal: "" });
  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    setLoadingPedidos(true);
    // Decisão D4 (auditoria 2026-08-07): a nota acompanha a MERCADORIA, então
    // o documento é elegível já na expedição — exigir "entregue" deixava o
    // dropdown vazio (nenhum pedido chegava a ENTREGUE) e o módulo fiscal
    // inteiro inutilizável.
    Promise.all([
      request("/api/pedidos?situacao=EM_EXPEDICAO").catch(() => []),
      request("/api/pedidos?situacao=ENTREGUE").catch(() => []),
    ])
      .then(([emExpedicao, entregues]) => {
        const lista = (p) => (Array.isArray(p) ? p : p?.results || []);
        setPedidos([...lista(emExpedicao), ...lista(entregues)]);
      })
      .catch(() => {})
      .finally(() => setLoadingPedidos(false));
  }, [request]);

  const submit = async () => {
    const payload = { pedido_id: d.pedido_id };
    if (d.cfop.trim()) payload.cfop = d.cfop.trim();
    if (d.natureza_operacao.trim()) payload.natureza_operacao = d.natureza_operacao.trim();
    if (d.observacao_fiscal.trim()) payload.observacao_fiscal = d.observacao_fiscal.trim();
    const nova = await criarNotaFiscal(request, payload);
    onCreated?.(nova);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Pedido *</span>
        <Select value={d.pedido_id} onChange={(e) => set("pedido_id", e.target.value)}>
          <option value="">{loadingPedidos ? "Carregando…" : "— selecione o pedido —"}</option>
          {pedidos.map((p) => (
            <option key={p.id} value={p.id}>
              {p.numero} · {p.cliente_nome} ({p.situacao_display || p.situacao})
            </option>
          ))}
        </Select>
        <span style={{ fontSize: 11, opacity: 0.6 }}>
          Pedidos em expedição ou já entregues — a nota acompanha a mercadoria.
        </span>
      </label>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>CFOP</span>
          <Input value={d.cfop} onChange={(e) => set("cfop", e.target.value)}
                 placeholder="padrão da config (ex.: 5102)" />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 600 }}>Natureza da operação</span>
          <Input value={d.natureza_operacao} onChange={(e) => set("natureza_operacao", e.target.value)}
                 placeholder="padrão da config" />
        </label>
      </div>

      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Observação fiscal (opcional)</span>
        <Textarea value={d.observacao_fiscal} rows={3} maxLength={500}
                  onChange={(e) => set("observacao_fiscal", e.target.value)}
                  placeholder="informações complementares que vão no corpo da NF-e" />
      </label>

      <div style={{
        padding: "10px 12px", background: "var(--info-bg, var(--surface-2))",
        border: "1px solid var(--border)", borderRadius: 8, fontSize: 12, color: "var(--ink-2)",
      }}>
        A nota nasce <strong>PENDENTE</strong>. Após criar, clique <strong>Emitir</strong> no
        detalhe para enviar ao gateway (SEFAZ) e obter a chave de acesso + XML/DANFE.
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button type="button" className="btn btn--secondary" onClick={onCancel}>Cancelar</button>
        <AsyncButton variant="primary" disabled={!d.pedido_id}
                     successMessage="NF-e criada — clique Emitir no detalhe"
                     onClick={submit}>
          <FiFilePlus size={14} aria-hidden="true" /> Criar NF-e
        </AsyncButton>
      </div>
    </div>
  );
}
